import { PagedListRequestInterface, PagedResponseInterface, PaginatedAPIInterface } from '@vendasta/galaxy/table';
import {
  App,
  AppPrice,
  AppPrices,
  AppPricingApiService,
  FieldMask,
  GetMultiPricingRequest,
  GetMultiPricingResponse,
  ItemsApiService,
  ListActivatableItemsRequest,
  ListActivatableItemsSortOptions,
  PartnerApiService,
  RequestedApp,
} from '@vendasta/marketplace-apps';
import {
  EditionsApiService,
  ListByMultiAppRequest,
  ListByMultiAppResponseAppEditions,
} from '@vendasta/marketplace-apps/v1';
import { combineLatest, merge, Observable, of } from 'rxjs';
import { catchError, map, shareReplay, skipWhile, startWith, switchMap } from 'rxjs/operators';
import { ItemSelectorConfig, ItemSelectorFilters, ItemSelectorRowItem, SelectedItem } from './interface';
import { ItemSelectorServiceV2 } from './item-selector-v2.service';
import { convertToItemSelectorEdition, findItem, findItemEdition, markActiveEdition } from './util';

export class ItemSelectorDataSource implements PaginatedAPIInterface<ItemSelectorRowItem, ItemSelectorFilters> {
  constructor(
    private readonly itemsApiService: ItemsApiService,
    private readonly appPricingApiService: AppPricingApiService,
    private readonly editionsService: EditionsApiService,
    private readonly sortOptions$: Observable<ListActivatableItemsSortOptions>,
    private readonly itemSelectorServiceV2: ItemSelectorServiceV2,
    private readonly immutableSelectedItems$: Observable<SelectedItem[]>,
    private readonly selectedItems$: Observable<SelectedItem[]>,
    private readonly config: Required<ItemSelectorConfig>,
    private readonly partnerApiService: PartnerApiService,
  ) {}

  get(
    pagedReq: PagedListRequestInterface<ItemSelectorFilters>,
  ): Observable<PagedResponseInterface<ItemSelectorRowItem>> {
    const activatableItems$ = this.sortOptions$.pipe(
      switchMap((sortOptions) => {
        const listReq = this.buildRequest(this.config.partnerId, this.config.marketId, pagedReq, sortOptions);
        return this.listActivatableItems(listReq);
      }),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    const rows$ = merge(
      activatableItems$,
      activatableItems$.pipe(switchMap((response) => this.sideloadData(response))),
    );

    return combineLatest([rows$, this.immutableSelectedItems$, this.selectedItems$]).pipe(
      map(([rows]) => {
        rows.data = this.processActivatableItems(rows.data);
        return rows;
      }),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );
  }

  private buildRequest(
    partnerId: string,
    marketId: string,
    pagedReq: PagedListRequestInterface<ItemSelectorFilters>,
    sortOptions: ListActivatableItemsSortOptions,
  ): ListActivatableItemsRequest {
    const cursor = pagedReq.pagingOptions?.cursor ?? '';
    const pageSize = pagedReq.pagingOptions?.pageSize ?? 0;
    const itemSelectorFilters = pagedReq?.filters?.[0];
    return new ListActivatableItemsRequest({
      sellerId: partnerId,
      storeId: marketId,
      cursor: cursor,
      pageSize: pageSize,
      filters: { searchTerm: pagedReq.searchOptions?.text, ...itemSelectorFilters },
      sortOptions: sortOptions,
    });
  }

  private listActivatableItems(
    req: ListActivatableItemsRequest,
  ): Observable<PagedResponseInterface<ItemSelectorRowItem>> {
    return this.itemsApiService.listActivatableItems(req).pipe(
      map((response) => {
        return {
          data: (response.activatableItemSummaries as ItemSelectorRowItem[]) ?? [],
          pagingMetadata: {
            nextCursor: response.nextCursor,
            hasMore: response.hasMore,
            totalResults: response.totalResults,
          },
        };
      }),
    );
  }

  private sideloadData(
    rows: PagedResponseInterface<ItemSelectorRowItem>,
  ): Observable<PagedResponseInterface<ItemSelectorRowItem>> {
    const pricing$ = this.getPricing(rows.data).pipe();
    const editions$ = this.getEditions(rows).pipe(startWith([]));
    const apps$ = this.getMarketplaceApps(rows.data).pipe(startWith([]));
    return combineLatest([pricing$, editions$, apps$]).pipe(
      skipWhile(([pricing, editions, apps]) => pricing.loading && !editions?.length && !apps?.length),
      map(([pricing, editions, apps]) => this.hydrateRows(rows, pricing, editions, apps)),
    );
  }

  private hydrateRows(
    rows: PagedResponseInterface<ItemSelectorRowItem>,
    pricing: GetItemPricingState,
    editions: ListByMultiAppResponseAppEditions[],
    apps: App[],
  ): PagedResponseInterface<ItemSelectorRowItem> {
    const pricingContexts = this.getPricingContexts();
    rows.data.forEach((rowItem: ItemSelectorRowItem) => {
      if (!pricing.loading) {
        rowItem.appPrices = this.getItemPrice(rowItem.itemId, pricing.prices, pricingContexts);
        rowItem.pricesInitialized = true;
      }
      if (editions.length > 0) {
        const rowItemEditions = editions.find((edition) => edition.appId === rowItem.itemId);
        if (rowItemEditions?.editions?.length) {
          rowItem.editions = rowItemEditions.editions.map(convertToItemSelectorEdition);
          const [item] = findItem(this.itemSelectorServiceV2.allItems(), rowItem.itemId);
          const edition = findItemEdition(item, rowItemEditions.editions);
          rowItem.editionId = edition?.editionId ?? '';
        }
      }
      if (apps?.length) {
        const app = apps.find((app) => app?.key?.appId === rowItem.itemId);
        rowItem.childApps = app?.childApps ?? [];
      }
    });
    return rows;
  }

  private getPricing(rows: ItemSelectorRowItem[]): Observable<GetItemPricingState> {
    const itemIds = rows.map((item) => item.itemId);
    if (!itemIds.length || (this.config.hideWholesale && this.config.hideRetail)) {
      return of({ prices: [], loading: false });
    }
    const request = new GetMultiPricingRequest({
      itemIds: itemIds,
      pricingContexts: new FieldMask({
        paths: this.getPricingContexts(),
      }),
      partnerId: this.config.partnerId,
      marketId: this.config.marketId,
      businessId: this.config.businessId,
    });
    if (this.config.priceType === 'instance') {
      request.requestedApps = rows.map((row) => new RequestedApp({ appId: row.itemId, quantity: row.quantity || 1 }));
      request.filterOptions = new FieldMask({ paths: ['instance_price'] });
    }
    return this.appPricingApiService.getMultiPricing(request).pipe(
      map<GetMultiPricingResponse, GetItemPricingState>((resp) => ({
        prices: resp?.appPrices ?? [],
        loading: false,
      })),
      catchError(() => {
        return of<GetItemPricingState>({ prices: [], loading: false });
      }),
      startWith<GetItemPricingState>({ prices: [], loading: true }),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );
  }

  private getPricingContexts(): string[] {
    const pricingContexts: string[] = [];
    if (!this.config.hideRetail) pricingContexts.push('retail');
    if (!this.config.hideWholesale) pricingContexts.push('wholesale');
    return pricingContexts;
  }

  private getEditions(
    rows: PagedResponseInterface<ItemSelectorRowItem>,
  ): Observable<ListByMultiAppResponseAppEditions[]> {
    const appIds = rows.data.filter((item) => item.usesEditions).map((item) => item.itemId);
    if (!appIds.length) {
      return of([]);
    }
    const request = new ListByMultiAppRequest({ appIds });
    return this.editionsService.listByMultiApp(request).pipe(
      map((resp) => resp?.appEditions),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );
  }

  private getMarketplaceApps(rows: ItemSelectorRowItem[]): Observable<App[]> {
    const getMultiAppRequest = {
      projectionFilter: {
        paths: ['childApps'],
      },
      appKeys: rows.map((row) => {
        return {
          appId: row.itemId,
          editionId: row.editionId,
        };
      }),
      partnerId: this.config.partnerId,
      marketId: this.config.marketId,
    };
    return this.partnerApiService.getMultiApp(getMultiAppRequest).pipe(
      map((response) => response.apps),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );
  }

  private processActivatableItems(activatableItems: ItemSelectorRowItem[]): ItemSelectorRowItem[] {
    return activatableItems.map((activatableItem) => {
      activatableItem = this.markActivatableItemAsSelected(activatableItem);
      activatableItem = this.markUnavailableItemInCountry(activatableItem, this.config.country);
      activatableItem = markActiveEdition(
        this.itemSelectorServiceV2.selectedItems(),
        this.itemSelectorServiceV2.immutableSelectedItems(),
        activatableItem,
      );
      return activatableItem;
    });
  }

  private markActivatableItemAsSelected(activatableItem: ItemSelectorRowItem): ItemSelectorRowItem {
    const [selectedItem] = findItem(this.itemSelectorServiceV2.selectedItems(), activatableItem.itemId);
    const [immutableItem] = findItem(this.itemSelectorServiceV2.immutableSelectedItems(), activatableItem.itemId);
    let isSelected = !!selectedItem || !!immutableItem;
    if (activatableItem.isMultiactivatable) {
      isSelected = !!selectedItem;
    }
    activatableItem.isSelected = isSelected;
    activatableItem.quantity = immutableItem?.immutable ? 0 : (selectedItem?.quantity ?? 0);
    activatableItem.immutable = immutableItem?.immutable;
    return activatableItem;
  }

  private markUnavailableItemInCountry(activatableItem: ItemSelectorRowItem, country?: string): ItemSelectorRowItem {
    activatableItem.unavailableInCountry = !this.isItemAvailableInCountry(activatableItem, country);
    return activatableItem;
  }

  private isItemAvailableInCountry(item: ItemSelectorRowItem, country?: string): boolean {
    if (!country || !item?.requiredCountries) return true;
    const matchesCountry = item?.requiredCountries?.some((c) => c.toUpperCase() === country.toUpperCase());
    return matchesCountry;
  }

  private getItemPrice(itemId: string, prices: AppPrices[], pricingContexts: string[]): AppPrices {
    const itemPrice = prices.find((price) => price.appId === itemId);
    if (itemPrice) return itemPrice;

    const unavailableItemPrice = new AppPrices({ appId: itemId, pricesForContexts: {} });
    pricingContexts.forEach((context) => {
      unavailableItemPrice.pricesForContexts[context] = new AppPrice({ appId: itemId });
    });
    return unavailableItemPrice;
  }
}

interface GetItemPricingState {
  prices: AppPrices[];
  loading: boolean;
}
